import { groupBy, mapValues } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { ComponentDesign } from '@paragon/contract-library';

import { RootState } from '../index';
import { AssemblyMemberPart, getAssemblyMemberParts } from './assemblyPartsUtilities';
import { getBundleByGuid } from './getBundleByGuid';
import { getComponentInventoryStockLengths } from './getComponentInventoryStockLengths';
import { getProductionAnalysisSetComponentDesignByAnalysisSetGuid } from './getProductionAnalysisSetComponentDesignByAnalysisSetGuid';

export interface BundleMemberPartEntry {
  readonly bundleId: number;
  readonly bundleAssemblyMemberParts: readonly AssemblyMemberPart[];
}

export const getBundleAssemblyMemberPartEntriesByBundleGuid = createSelector(
  (state: RootState) => state.productionModels.orderedProductionComponentInstancesByBundleGuid,
  getBundleByGuid,
  getProductionAnalysisSetComponentDesignByAnalysisSetGuid,
  getComponentInventoryStockLengths,

  (
    orderedProductionComponentInstancesByBundleGuid,
    bundlesByGuid,
    productionAnalysisSetComponentDesignByAnalysisSetGuid,
    stockLengths,
  ): { [bundleGuid: string]: BundleMemberPartEntry } =>
    mapValues(bundlesByGuid, (bundle) => {
      const instancesForBundle = orderedProductionComponentInstancesByBundleGuid[bundle.guid];
      const analysisSetGuidQuantities = mapValues(
        groupBy(instancesForBundle, ({ analysisSetGuid }) => analysisSetGuid),
        (instances) => instances.length,
      );
      const uniqueAnalysisSetGuids = Object.keys(analysisSetGuidQuantities);
      const analysisSetGuidToComponentDesign = uniqueAnalysisSetGuids.reduce(
        (accumulator, analysisSetGuid) => {
          if (analysisSetGuid in productionAnalysisSetComponentDesignByAnalysisSetGuid)
            accumulator[analysisSetGuid] = productionAnalysisSetComponentDesignByAnalysisSetGuid[analysisSetGuid];
          return accumulator;
        },
        {} as { [analysisSetGuid: string]: ComponentDesign },
      );

      const bundleAssemblyMemberParts = getAssemblyMemberParts(
        analysisSetGuidQuantities,
        analysisSetGuidToComponentDesign,
        stockLengths,
        'asc',
      );

      const bundleMemberPartEntry: BundleMemberPartEntry = {
        bundleId: bundlesByGuid[bundle.guid].id,
        bundleAssemblyMemberParts,
      };
      return bundleMemberPartEntry;
    }),
);
